import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';

import {
    activeSubAccountSelector,
    createZoneShare,
    deleteZoneShare,
    changeView,
} from '../../features/subaccounts/subaccountSlice';
import { zonesSelector } from '../../features/places/placesSlice';

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(248,250,252,0.98), rgba(226,232,240,0.95))',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
        overflow: 'hidden',
    },
    dialogTitle: {
        padding: '20px 24px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    titleText: {
        fontWeight: 700,
        letterSpacing: '0.02em',
    },
    subtitleText: {
        fontSize: '0.8rem',
        color: 'rgba(226, 232, 240, 0.8)',
    },
    searchField: {
        minWidth: 240,
        backgroundColor: 'rgba(248, 250, 252, 0.95)',
        borderRadius: 12,
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.4)',
        },
        '& .MuiInputBase-input': {
            fontSize: '0.9rem',
        },
    },
    closeButton: {
        padding: 6,
        borderRadius: 10,
        border: '1px solid rgba(148, 163, 184, 0.4)',
        color: '#e2e8f0',
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
        },
    },
    content: {
        padding: 20,
        paddingTop: 36,
    },
    panel: {
        borderRadius: 16,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        minHeight: 320,
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    panelTitle: {
        fontWeight: 600,
        color: '#0f172a',
    },
    selectAllButton: {
        textTransform: 'none',
        borderRadius: 10,
        fontWeight: 600,
        background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)',
        color: '#ffffff',
        boxShadow: '0 10px 18px rgba(14, 165, 233, 0.2)',
        '&:hover': {
            background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
        },
    },
    listGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 12,
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    },
    itemShared: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: 'rgba(15, 23, 42, 0.7)',
        color: '#f8fafc',
    },
    itemText: {
        fontSize: '0.92rem',
        fontWeight: 600,
        color: 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    emptyText: {
        color: '#64748b',
        fontSize: '0.85rem',
    },
}));

const SubAccountZones = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const zones = useSelector(zonesSelector);
    const activeSubAccount = useSelector(activeSubAccountSelector);

    const [zoneList, setZoneList] = useState([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (activeSubAccount && activeSubAccount.zones && activeSubAccount.zones.length > 0) {
            setZoneList([...activeSubAccount.zones.map((z) => `${z.zone_id}`)]);
        } else {
            setZoneList([]);
        }
    }, [activeSubAccount]);

    const handleToggle = (zoneId, nextChecked) => {
        if (!activeSubAccount || !activeSubAccount.subuser_id) {
            return;
        }
        const shouldShare = typeof nextChecked === 'boolean'
            ? nextChecked
            : zoneList.indexOf(zoneId) === -1;
        if (!shouldShare) {
            setZoneList((prev) => prev.filter((id) => id !== zoneId));
            dispatch(deleteZoneShare({ subuser_id: activeSubAccount.subuser_id, zoneids: [zoneId] }));
        } else {
            setZoneList((prev) => [...prev, zoneId]);
            dispatch(createZoneShare({ subuser_id: activeSubAccount.subuser_id, zoneids: [zoneId] }));
        }
    };

    const allZoneIds = useMemo(() => Object.keys(zones || {}), [zones]);
    const allSelected = allZoneIds.length > 0 && allZoneIds.every((id) => zoneList.indexOf(id) !== -1);

    const handleSelectAllToggle = () => {
        if (!activeSubAccount || !activeSubAccount.subuser_id) {
            return;
        }
        if (allSelected) {
            setZoneList([]);
            if (allZoneIds.length > 0) {
                dispatch(deleteZoneShare({ subuser_id: activeSubAccount.subuser_id, zoneids: allZoneIds }));
            }
        } else {
            setZoneList(allZoneIds);
            if (allZoneIds.length > 0) {
                dispatch(createZoneShare({ subuser_id: activeSubAccount.subuser_id, zoneids: allZoneIds }));
            }
        }
    };

    const visibleZonesList = useMemo(() => {
        if (!zones) {
            return [];
        }
        if (search.length > 2) {
            const matches = Object.keys(zones).filter((id) =>
                zones[id].zone_name.toUpperCase().includes(search.toUpperCase())
            );
            return [...new Set([...matches, ...zoneList])];
        }
        return Object.keys(zones);
    }, [zones, search, zoneList]);

    const handleClose = () => {
        dispatch(changeView('main'));
    };

    if (!zones || Object.keys(zones).length === 0 || !activeSubAccount) {
        return null;
    }

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.titleRow}>
                    <Box>
                        <Typography variant="h6" className={classes.titleText}>
                            Zone Selection
                        </Typography>
                        <Typography className={classes.subtitleText}>
                            {activeSubAccount?.person_name || 'Sub Account'}
                        </Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <TextField
                            className={classes.searchField}
                            size="small"
                            variant="outlined"
                            placeholder="Search zones"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon path={mdiMagnify} size={0.8} color="#64748b" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton onClick={handleClose} className={classes.closeButton}>
                            <Icon path={mdiClose} size={0.8} color="currentColor" />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Box className={classes.panel}>
                    <Box className={classes.panelHeader}>
                        <Typography className={classes.panelTitle}>
                            Zones Shared - {zoneList.length}
                        </Typography>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Typography variant="caption" color="text.secondary">
                                {visibleZonesList.length} total
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleSelectAllToggle}
                                variant="contained"
                                className={classes.selectAllButton}
                            >
                                {allSelected ? 'Clear All' : 'Share All'}
                            </Button>
                        </Box>
                    </Box>
                    <Divider />
                    <Box className={classes.listGrid}>
                        {visibleZonesList.length > 0 ? (
                            visibleZonesList.map((zoneId) => {
                                const zone = zones[zoneId];
                                if (!zone) {
                                    return null;
                                }
                                const isShared = zoneList.indexOf(zoneId) !== -1;
                                return (
                                    <Box
                                        key={zoneId}
                                        className={`${classes.item} ${isShared ? classes.itemShared : ''}`}
                                    >
                                        <Typography className={classes.itemText}>
                                            {zone.zone_name}
                                        </Typography>
                                        <Switch
                                            checked={isShared}
                                            onChange={(event, checked) => handleToggle(zoneId, checked)}
                                            sx={{
                                                '& .MuiSwitch-thumb': {
                                                    color: isShared ? '#f8fafc' : '#0ea5e9',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: isShared
                                                        ? 'rgba(248, 250, 252, 0.35)'
                                                        : 'rgba(148, 163, 184, 0.4)',
                                                },
                                                '& .Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: isShared ? '#94a3b8' : '#38bdf8',
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography className={classes.emptyText}>
                                No zones found.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SubAccountZones;
